import { useState, useEffect, useCallback } from "react";
import { Stethoscope, Loader2, CheckCircle, Sparkles, AlertTriangle, ArrowRight, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AppLayout from "@/components/AppLayout";
import { uid } from "./clinical/constants";
import ClinicalProofTab from "./clinical/ClinicalProofTab";
import RegulatoryRouteTab from "./clinical/RegulatoryRouteTab";
import AccessHeorTab from "./clinical/AccessHeorTab";
import StrategyTabBar from "./StrategyTabBar";
import ClusteredTodoTab from "./ClusteredTodoTab";
import { useStrategyModule } from "@/hooks/useStrategyModule";

const VALID_TABS = new Set(["clinical_proof", "regulatory_route", "access_heor", "todos"]);
const SUB_TOPICS = [
  { key: "clinical_proof",   label: "Clinical Proof" },
  { key: "regulatory_route", label: "Regulatory Route" },
  { key: "access_heor",      label: "Access & HEOR" },
];
const HEADERS = {
  clinical_proof:   { title: "Clinical Proof",   subtitle: "Target patient population, minimum viable profile and next clinical milestone — grounded in ClinicalTrials.gov." },
  regulatory_route: { title: "Regulatory Route", subtitle: "Designations, primary agency and pathway strategy — informed by FDA/EMA/MHRA precedents." },
  access_heor:      { title: "Access & HEOR",    subtitle: "Value proposition, comparator and payer rationale — anchored in NICE and payer decisions." },
  todos:            { title: "To Do",            subtitle: "Your top 5 priorities across the three topics. Generate a Clinical & Regulatory strategic overview when you're ready." },
};

const EMPTY_CP = { patient_population: "", why_this_first: "", current_alternative: "", minimum_profile: "", ideal_profile: "", tpp_table: [], current_stage: "", next_milestone: "", proposed_endpoint: "", success_criteria: "", key_questions: [], comparable_trials: [] };
const EMPTY_RR = { product_type: "", designations_eligible: "", designation_status: "", primary_agency: "", pathway_strategy: "", agency_interactions_planned: "", cmc_readiness: "", key_risks: "", mitigations: "", key_questions: [], precedent_decisions: [] };
const EMPTY_AH = { outcome_improved: "", best_patients: "", value_statement: "", value_categories: [], comparator: "", replaces_cost: "", additional_costs: "", why_payer_fund: "", evidence: [], key_questions: [], nice_appraisals: [] };

export default function ClinicalStrategy() {
  const shell = useStrategyModule({ endpointKey: "clinical", defaultTab: "clinical_proof", validTabs: VALID_TABS });
  const { user, companyProfile, loading, saving, activeTab, setActiveTab, urlTab, followups, setFollowups, card, setCard, cardOpen, setCardOpen, generatingCard, fetchProgress, postProgress, postGenerateCard } = shell;

  const [cp, setCp] = useState(EMPTY_CP);
  const [rr, setRr] = useState(EMPTY_RR);
  const [ah, setAh] = useState(EMPTY_AH);

  useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    const d = await fetchProgress();
    if (!d) return;
    if (d.clinical_proof) setCp({ ...EMPTY_CP, ...d.clinical_proof });
    if (d.regulatory_route) setRr({ ...EMPTY_RR, ...d.regulatory_route });
    if (d.access_heor) setAh({ ...EMPTY_AH, ...d.access_heor });
    setFollowups((d.followups || []).map((f) => ({ id: f.id || uid(), text: f.text || "", done: !!f.done, sub_module: f.sub_module || null })));
    if (d.card) setCard(d.card);
    if (d.activeView && VALID_TABS.has(d.activeView) && !urlTab) setActiveTab(d.activeView);
  };

  const persist = useCallback(async (overrides = {}, showToast = false) => {
    const payload = { clinical_proof: cp, regulatory_route: rr, access_heor: ah, followups, card, activeView: activeTab, ...overrides };
    await postProgress(payload, showToast);
  }, [cp, rr, ah, followups, card, activeTab, postProgress]);

  const generateCard = async () => {
    const c = await postGenerateCard({ progress: { clinical_proof: cp, regulatory_route: rr, access_heor: ah } });
    if (c) persist({ card: c });
  };

  if (loading) return <AppLayout user={user}><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div></AppLayout>;

  return (
    <AppLayout user={user}>
      <div className="max-w-[1400px]" data-testid="clinical-strategy-page">
        <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center"><Stethoscope className="w-5 h-5 text-teal-600" /></div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{HEADERS[activeTab]?.title || "Clinical & Regulatory"}</h1>
              <p className="text-sm text-slate-500">{HEADERS[activeTab]?.subtitle}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => persist({}, true)} data-testid="cr-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />} Save
          </Button>
        </div>

        <div data-testid={`cr-panel-${activeTab}`} className="space-y-8">
          <StrategyTabBar
            tabs={SUB_TOPICS}
            activeTab={activeTab}
            onSelect={setActiveTab}
            todoCount={followups.filter((f) => !f.done).length}
          />

          {activeTab === "clinical_proof"   && <ClinicalProofTab   data={cp} setData={setCp} persist={persist} companyProfile={companyProfile} />}
          {activeTab === "regulatory_route" && <RegulatoryRouteTab data={rr} setData={setRr} persist={persist} companyProfile={companyProfile} />}
          {activeTab === "access_heor"      && <AccessHeorTab      data={ah} setData={setAh} persist={persist} companyProfile={companyProfile} />}
          {activeTab === "todos" && (
            <ClusteredTodoTab
              items={followups}
              setItems={setFollowups}
              persist={persist}
              endpointKey="clinical"
              getSuggestBody={(existing) => ({
                progress: { clinical_proof: cp, regulatory_route: rr, access_heor: ah },
                existing,
              })}
              onGenerateCard={generateCard}
              generatingCard={generatingCard}
              hasCard={!!card}
              onOpenCard={() => setCardOpen(true)}
              groupTitle="Generate Clinical & Regulatory overview"
            />
          )}
        </div>
      </div>

      <Dialog open={cardOpen} onOpenChange={(o) => { if (!o) setCardOpen(false); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cr-card-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> Clinical & Regulatory card</DialogTitle>
              <Button variant="outline" size="sm" onClick={() => window.print()}><Download className="w-4 h-4 mr-2" /> Print / PDF</Button>
            </div>
            <DialogDescription className="sr-only">Generated Clinical & Regulatory card.</DialogDescription>
          </DialogHeader>
          {!card ? <div className="py-8 text-center text-slate-500">No card yet.</div> : (
            <div className="space-y-4 text-sm">
              <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                <h3 className="text-lg font-bold text-teal-900">{companyProfile?.company_name}</h3>
                <p className="text-teal-700 mt-2">{card.executive_summary}</p>
              </div>
              {card.clinical_proof_summary && <Card><CardContent className="pt-4"><h4 className="text-sm font-medium text-slate-700 mb-1">Clinical Proof</h4><p className="text-slate-600">{card.clinical_proof_summary}</p></CardContent></Card>}
              {card.regulatory_route_summary && <Card><CardContent className="pt-4"><h4 className="text-sm font-medium text-slate-700 mb-1">Regulatory Route</h4><p className="text-slate-600">{card.regulatory_route_summary}</p></CardContent></Card>}
              {card.access_heor_summary && <Card><CardContent className="pt-4"><h4 className="text-sm font-medium text-slate-700 mb-1">Access & HEOR</h4><p className="text-slate-600">{card.access_heor_summary}</p></CardContent></Card>}
              {card.key_risks?.length > 0 && (<div className="border rounded-lg p-4 border-amber-200 bg-amber-50"><h4 className="text-sm font-medium text-amber-900 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Key risks</h4><ul className="space-y-1">{card.key_risks.map((r, i) => <li key={i} className="text-amber-800">• {r}</li>)}</ul></div>)}
              {card.evidence_gaps?.length > 0 && (<div className="border rounded-lg p-4"><h4 className="text-sm font-medium text-slate-700 mb-2">Evidence gaps</h4><ul className="space-y-1">{card.evidence_gaps.map((r, i) => <li key={i} className="text-slate-600">• {r}</li>)}</ul></div>)}
              {card.next_90_days?.length > 0 && (<div className="border rounded-lg p-4 bg-teal-50 border-teal-200"><h4 className="text-sm font-medium text-teal-900 mb-2 flex items-center gap-2"><ArrowRight className="w-4 h-4" /> 90-day actions</h4><div className="space-y-2">{card.next_90_days.map((a, i) => <div key={i} className="bg-white p-2 rounded border border-teal-100"><Badge variant="outline" className="text-xs mr-2">Next</Badge><span className="font-medium text-teal-900">{a.action}</span><p className="text-slate-600 text-xs mt-1">{a.outcome}</p></div>)}</div></div>)}
              <div className="flex justify-end pt-2 border-t"><Button onClick={generateCard} disabled={generatingCard}>{generatingCard ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />} Regenerate</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
